<script lang="ts" module>
  export type AddressFieldErrors = Partial<Record<keyof CartCommandAddress, string>>;
</script>

<script lang="ts">
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { TextField } from '@athena-lib/Fields';
  import type {
    CartCommandAddress,
    CheckoutInstance,
    CountriesResponse,
  } from '@stickerapp-org/checkout-sdk';
  import Dropdown from '@athena-lib/Fields/Dropdown/Dropdown.svelte';
  import { Text } from '@athena-lib/Text';

  import Modal from '@athena-lib/Modal/Modal.svelte';
  import Button from '@athena-lib/Button/Button/Button.svelte';
  import SuggestiveStreetAddress from './SuggestiveStreetAddress.svelte';
  import type { CustomerForm } from './checkoutForm.svelte';

  let {
    formFields,
    type,
    hideNameFields,
    countries,
    updateCountry,
    checkoutCountry,
  }: {
    formFields: CustomerForm;
    type: 'billing' | 'shipping';
    countries?: CountriesResponse;
    updateCountry: CheckoutInstance['updateCountry'];
    hideNameFields?: boolean;
    checkoutCountry: string;
  } = $props();
  let showSecondaryAddress = $state(!!formFields[`${type} address-line2`].value);
  const translations = getCommonTranslations();
  let shipToAnotherModal = $state<{ type: 'open'; value: string } | { type: 'closed' }>({
    type: 'closed',
  });

  const onUpdateCountry = async (countryCode: string, currency: string) => {
    try {
      await updateCountry(countryCode, currency);
      formFields[`${type} address-line1`].value = '';
      formFields[`${type} address-line2`].value = '';
      formFields[`${type} address-level2`].value = '';
      formFields[`${type} postal-code`].value = '';
      formFields[`${type} address-level1`].value = '';
      formFields[`${type} address-level1`].onchange();
    } catch (err) {
      console.error('Could not change country code', err);
    }
  };
  const getStates = (countryCode: string) =>
    countries?.allCountries.find((x) => x.code.toLowerCase() === countryCode.toLowerCase());
  const states = $derived(
    Object.values(
      getStates(type === 'billing' ? formFields[`${type} country`].value : checkoutCountry)
        ?.states ?? {},
    ),
  );
</script>

{#if !hideNameFields}
  <div class="flex flex-col gap-2 md:flex-row">
    <TextField
      autocomplete={`${type} given-name`}
      name="first-name"
      {...formFields[`${type} given-name`]}
      bind:value={formFields[`${type} given-name`]['value']}
      class="md:basis-1/2"
    />
    <TextField
      autocomplete={`${type} family-name`}
      name="last-name"
      {...formFields[`${type} family-name`]}
      bind:value={formFields[`${type} family-name`]['value']}
      class="md:basis-1/2"
    />
  </div>
{/if}
<div>
  <SuggestiveStreetAddress
    {type}
    {...formFields[`${type} address-line1`]}
    bind:streetAddress={formFields[`${type} address-line1`]['value']}
    country={type === 'billing'
      ? formFields[`${type} country`].value || checkoutCountry
      : checkoutCountry}
    onPickAddress={(addr) => {
      formFields[`${type} address-line1`].value = addr.address1;
      formFields[`${type} address-level2`].value = addr.city;
      formFields[`${type} postal-code`].value = addr.postalCode;
      if (type === 'billing') {
        formFields[`${type} country`].value = addr.country_code.toLowerCase();
      }
      formFields[`${type} address-level1`].value = states.length > 0 ? addr.stateOrProvince : '';
      formFields[`${type} address-line1`].onchange();
    }}
  />
  {#if showSecondaryAddress}
    <TextField
      autocomplete={`${type} address-line2`}
      class="mt-4"
      name="street-address2"
      {...formFields[`${type} address-line2`]}
      bind:value={formFields[`${type} address-line2`]['value']}
    />
  {:else}
    <div class="flex justify-start">
      <button
        type="button"
        class="pt-1 text-xs underline"
        onclick={() => {
          showSecondaryAddress = true;
        }}
      >
        {translations.addAddressLine2}
      </button>
    </div>
  {/if}
</div>
<div class="flex flex-col gap-2 md:flex-row">
  <TextField
    adornment={type === 'shipping' ? checkoutCountry.toUpperCase() : undefined}
    autocomplete={`${type} postal-code`}
    name="postal-code"
    {...formFields[`${type} postal-code`]}
    bind:value={formFields[`${type} postal-code`]['value']}
    class="md:basis-6/12"
  />
  <TextField
    autocomplete={`${type} address-level2`}
    name="city"
    class="md:basis-6/12"
    {...formFields[`${type} address-level2`]}
    bind:value={formFields[`${type} address-level2`]['value']}
  />
</div>

{#if states.length > 0}
  <Dropdown
    autocomplete={`${type} address-level1`}
    name="state"
    options={states.map((x) => ({ label: x.name, value: x.code }))}
    {...formFields[`${type} address-level1`]}
    bind:value={formFields[`${type} address-level1`]['value']}
  />
{/if}

{#if type === 'billing'}
  <Dropdown
    name="country"
    options={countries?.allCountries.map((x) => ({ label: x.name, value: x.code.toLowerCase() })) ??
      []}
    {...formFields[`${type} country`]}
    bind:value={formFields[`${type} country`]['value']}
  />
{:else}
  <div>
    <button
      type="button"
      class="underline"
      onclick={() => {
        shipToAnotherModal = { type: 'open', value: checkoutCountry };
      }}>{translations.shipToAnotherCountry}</button
    >
  </div>
  <Modal
    showModal={shipToAnotherModal.type === 'open'}
    onClose={() => {
      shipToAnotherModal = { type: 'closed' };
    }}
    size="sm"
    closable={true}
  >
    {#if shipToAnotherModal.type === 'open'}
      <div class="flex flex-col gap-4 p-4">
        <Text bold>{translations.shipToAnotherCountryTitle}</Text>
        <div>{translations.shipToAnotherCountryDisclaimer}</div>
        <Dropdown
          name="country"
          label={translations.country}
          bind:value={shipToAnotherModal.value}
          options={countries?.shippingCountries.map((x) => ({
            label: x.name,
            value: x.code.toLowerCase(),
            disabled: !x.active,
          })) ?? []}
        />
        <Button
          type="button"
          onClick={() => {
            const needle =
              shipToAnotherModal.type === 'open' ? shipToAnotherModal.value : undefined;
            const foundCountry = countries?.shippingCountries.find(
              (x) => x.code.toLowerCase() === needle,
            );
            if (foundCountry) {
              onUpdateCountry(foundCountry.code, foundCountry.currency);
              shipToAnotherModal = { type: 'closed' };
            }
          }}>{translations.continue}</Button
        >
      </div>
    {/if}
  </Modal>
{/if}
